import { useEffect } from 'react';
import { X } from 'lucide-react';
import { formatDateTime } from '../../utils/formatters';

export type EmailBounceDetailView = {
  /** Hotel name or similar context shown under the dialog title. */
  contextTitle: string;
  emailKindLabel: string;
  detail: string;
  occurredAt: string | null;
};

type EmailBounceDetailModalProps = {
  view: EmailBounceDetailView | null;
  onClose: () => void;
};

export function EmailBounceDetailModal({ view, onClose }: EmailBounceDetailModalProps) {
  useEffect(() => {
    if (!view) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    globalThis.addEventListener('keydown', onKeyDown);
    return () => globalThis.removeEventListener('keydown', onKeyDown);
  }, [view, onClose]);

  if (!view) {
    return null;
  }

  return (
    <div
      className="fixed inset-0 z-[70] flex items-center justify-center p-4"
      role="presentation"
    >
      <button
        type="button"
        className="absolute inset-0 cursor-pointer bg-black/50"
        aria-label="Close bounce details"
        onClick={onClose}
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="email-bounce-detail-title"
        className="relative z-10 w-full max-w-md overflow-hidden rounded-xl bg-white shadow-xl ring-1 ring-gray-200/80"
      >
        <div className="flex items-start justify-between gap-3 border-b border-gray-200 px-4 py-3 pr-12">
          <div className="min-w-0">
            <h2 id="email-bounce-detail-title" className="text-sm font-semibold text-gray-900">
              Email bounce details
            </h2>
            <p className="mt-0.5 truncate text-xs text-gray-600" title={view.contextTitle}>
              {view.contextTitle}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="absolute right-3 top-3 rounded-md p-1 text-gray-500 hover:bg-gray-100 hover:text-gray-800"
            aria-label="Close"
          >
            <X className="h-4 w-4" aria-hidden />
          </button>
        </div>
        <div className="space-y-3 px-4 py-4 text-xs">
          <div>
            <div className="text-[10px] font-semibold uppercase tracking-wide text-gray-500">
              Email
            </div>
            <div className="mt-0.5 font-medium text-gray-900">{view.emailKindLabel}</div>
            {view.occurredAt ? (
              <div className="mt-0.5 tabular-nums text-gray-500">{formatDateTime(view.occurredAt)}</div>
            ) : null}
          </div>
          <div>
            <div className="text-[10px] font-semibold uppercase tracking-wide text-gray-500">
              Postmark reason
            </div>
            <p className="mt-1 whitespace-pre-wrap break-words leading-relaxed text-gray-800">
              {view.detail}
            </p>
          </div>
        </div>
        <div className="border-t border-gray-100 bg-gray-50/80 px-4 py-3 text-right">
          <button type="button" onClick={onClose} className="btn-secondary-sm">
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
